import { truncate } from "./chart-utils";
import { scoreHex } from "./score-colors";
import type { ChartLocation } from "./useChartData";

interface RiskViewProps {
  locations: ChartLocation[];
}

interface RiskEntry {
  id: string;
  addressRaw: string;
  totalScore: number;
  weakestCategory: string;
  weakestScore: number;
}

function buildRiskEntries(locations: ChartLocation[]): RiskEntry[] {
  const entries: RiskEntry[] = [];

  for (const loc of locations) {
    if (loc.dataCoverage === 0) continue;

    let weakest: { category: string; score: number } | null = null;
    for (const cs of loc.categoryScores) {
      if (!weakest || cs.score < weakest.score) {
        weakest = { category: cs.categoryName, score: cs.score };
      }
    }

    if (weakest) {
      entries.push({
        id: loc.id,
        addressRaw: loc.addressRaw,
        totalScore: loc.totalScore,
        weakestCategory: weakest.category,
        weakestScore: Math.round(weakest.score),
      });
    }
  }

  return entries.sort((a, b) => a.weakestScore - b.weakestScore).slice(0, 10);
}

export function RiskView({ locations }: RiskViewProps) {
  const entries = buildRiskEntries(locations);

  if (entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">No risk data available.</p>
    );
  }

  const maxScore = 100;

  return (
    <div className="space-y-2">
      {entries.map((entry) => {
        const catLabel = entry.weakestCategory;
        const barColor = scoreHex(entry.weakestScore);

        return (
          <div
            key={entry.id}
            className="flex items-center gap-3 rounded-lg border border-border bg-card p-3"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" title={entry.addressRaw}>
                {truncate(entry.addressRaw, 37)}
              </p>
              <p className="text-xs text-muted-foreground">
                Overall: {Math.round(entry.totalScore)}
              </p>
            </div>

            <div className="w-48 shrink-0">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground">{catLabel}</span>
                <span className="text-xs font-medium tabular-nums">{entry.weakestScore}</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${(entry.weakestScore / maxScore) * 100}%`,
                    backgroundColor: barColor,
                  }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
